<script setup lang="ts">
const CONSENT_COOKIE_NAME = 'GDPR_CONSENT'
const CONSENT = {
  ACCEPT: 'ACCEPT_ALL',
  ACCEPT_NECESSARY: 'ACCEPT_NECESSARY',
} as const

type ConsentTypes = (typeof CONSENT)[keyof typeof CONSENT]

const { grantConsent, revokeConsent } = useGtag()
const runtimeConfig = useRuntimeConfig()
const loadSegment = useLoadSegment(runtimeConfig.public.segmentWriteKey)

const consentModalIsOpen = ref(false)
// Max value for maxAge
// see: https://stackoverflow.com/questions/3290424/set-a-cookie-to-never-expire
const consentCookie = useCookie(CONSENT_COOKIE_NAME, { maxAge: 2147483647 })
// used to track unsaved changes
const localConsentValue = ref(consentCookie.value || '')

const handleSave = (): void => {
  consentCookie.value = localConsentValue.value
  closeModal()
}

const accept = (value: ConsentTypes): void => {
  consentCookie.value = value
  closeModal()
}

const handleCancel = (): void => {
  closeModal()
}

const openModal = (): void => {
  consentModalIsOpen.value = true
  localConsentValue.value = consentCookie.value || ''
}

const closeModal = (): void => {
  consentModalIsOpen.value = false
  if (consentCookie.value === CONSENT.ACCEPT) {
    initThirdPartyScripts()
  } else {
    stopThirdPartyScripts()
  }
}

const initThirdPartyScripts = (): void => {
  // Gtag
  grantConsent()
  loadSegment()
}

const stopThirdPartyScripts = (): void => {
  // Gtag
  revokeConsent()
}

onMounted(() => {
  // if the cookie has not been defined yet, show modal
  if (!consentCookie.value) {
    return openModal()
  }

  if (consentCookie.value === CONSENT.ACCEPT) {
    initThirdPartyScripts()
  }
})
</script>

<template>
  <!-- to avoid hydration errors -->
  <ClientOnly fallback-tag="span">
    <Teleport to="body">
      <UiModal
        title="consent-message"
        data-test="consent-message"
        :is-open="consentModalIsOpen"
        :has-close="!!localConsentValue"
        :should-close-on-esc="!!localConsentValue"
        @close="consentModalIsOpen = false"
      >
        <template #title>
          <h2 class="cookie-consent__heading">
            <Icon
              class="cookie-icon"
              name="fa6-solid:cookie-bite"
            />
            <span>We're using cookies</span>
          </h2>
        </template>
        <p>
          Hey there! We need cookies to help our App Store run as smoothly as
          possible. Some cookies are required just to make the page function.
          Then there are cookies that help us understand how you're using the
          page, so that we can improve your experience in the future.
        </p>
        <fieldset
          v-if="localConsentValue"
          class="cookie-consent__radios"
        >
          <label class="input-label--radio">
            <input
              v-model="localConsentValue"
              data-test="functional-cookies-option"
              type="radio"
              name="consent"
              :value="CONSENT.ACCEPT_NECESSARY"
            >
            Accept only functional cookies
          </label>
          <label class="input-label--radio">
            <input
              v-model="localConsentValue"
              data-test="all-cookies-option"
              type="radio"
              name="consent"
              :value="CONSENT.ACCEPT"
            >
            Accept all cookies
          </label>
        </fieldset>
        <p>
          Read more about our cookies in the
          <a
            href="https://www.sendcloud.com/privacy-policy/"
            target="_blank"
            rel="noopener noreferrer"
          >privacy statement</a>
        </p>
        <div
          v-if="localConsentValue"
          class="button-container"
        >
          <UiButton
            data-test="save-cookies-preferences"
            type="primary"
            tabindex="0"
            @click="handleSave"
          >
            Save
          </UiButton>
          <UiButton
            data-test="cancel-cookies-preferences"
            type="secondary"
            @click="handleCancel"
          >
            Cancel
          </UiButton>
        </div>
        <div
          v-else
          class="button-container"
        >
          <UiButton
            data-test="accept-all-cookies-button"
            type="primary"
            tabindex="0"
            @click="accept(CONSENT.ACCEPT)"
          >
            Accept all
          </UiButton>
          <UiButton
            data-test="accept-functional-cookies-button"
            type="secondary"
            @click="accept(CONSENT.ACCEPT_NECESSARY)"
          >
            Accept functional
          </UiButton>
        </div>
      </UiModal>
    </Teleport>
  </ClientOnly>
  <UiButton
    data-test="manage-cookies-preferences"
    class="cookie-consent__manage-button"
    type="footer-link"
    @click="openModal"
  >
    Manage cookie preferences
  </UiButton>
</template>

<style lang="scss" scoped>
@use '@/assets/styles/helpers' as *;

.cookie-consent__heading {
  margin: 0;
}

.cookie-icon {
  margin-right: 8px;
}

.cookie-consent__radios {
  border: 0;
  display: grid;
  gap: var(--app-spacer-8);
  margin: 0;
  padding: 0;
}

.button-container {
  display: flex;
  gap: var(--app-spacer-16);

  @include max-width(md) {
    flex-direction: column;
  }
}

.input-label--radio {
  display: flex;
  gap: var(--app-spacer-8);
  align-items: center;
  cursor: pointer;
}

// higher specificity for SSR
// (gets overridden by default btn styles)
button.cookie-consent__manage-button {
  padding: var(--app-spacer-8);
  margin-bottom: var(--app-spacer-16);
}
</style>
